import { useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { useSiteContent } from "@/contexts/SiteContentContext";
import {
  Wifi, Coffee, Snowflake, Car, Bot, Plane, Dog, Mountain, Sparkles, ExternalLink,
  Scissors, Utensils, Package, ShoppingCart, Bike, Ship, Camera, Music,
  Heart, Flower2, Wine, Cake, Pizza, IceCream, Beer, Gift, Stethoscope, Pill,
  Baby, Shirt, Wrench, Bath, Bed, Briefcase, MapPin, Phone, MessageCircle, Tv,
  Gamepad2, BookOpen, Sun, Waves, X, Receipt,
} from "lucide-react";

const ICONS = {
  wifi: Wifi, coffee: Coffee, ac: Snowflake, parking: Car,
  concierge: Bot, transfer: Plane, pet: Dog, tour: Mountain,
  // Beauty & wellness
  parrucchiera: Scissors, scissors: Scissors, spa: Flower2, massaggio: Heart, bath: Bath,
  // Food & drink
  ristorante: Utensils, food: Utensils, pizza: Pizza, gelato: IceCream, beer: Beer, wine: Wine, cake: Cake,
  // Delivery & shopping
  delivery: Package, package: Package, shopping: ShoppingCart, spesa: ShoppingCart, gift: Gift,
  // Transport & active
  bike: Bike, boat: Ship, mare: Waves, sole: Sun,
  // Health
  doctor: Stethoscope, farmacia: Pill, baby: Baby,
  // Other
  laundry: Shirt, maintenance: Wrench, bed: Bed, work: Briefcase,
  photo: Camera, music: Music, location: MapPin, call: Phone, chat: MessageCircle,
  tv: Tv, games: Gamepad2, books: BookOpen, tax: Receipt,
};

// Default deep links per service type (used if the admin hasn't set a custom URL).
// Aggiornato su richiesta utente: ogni servizio porta alla sezione/funzionalità
// più affine sul sito, così il cliente può vedere "concretamente" cosa offriamo.
const DEFAULT_LINKS = {
  concierge: "#chat",            // opens Tawk chat
  transfer: "/shuttle-request",  // shuttle request form
  parking: "#contact",           // parking is "on request" → contact section
  wifi: "#gallery",              // gallery shows rooms with Wi-Fi signal
  coffee: "#gallery",            // gallery shows the breakfast room
  ac: "#gallery",                // gallery shows rooms with AC
  pet: "/booking?showPetPolicy=1", // booking page → pet policy / rules
  tour: "#gallery",              // angolo cottura → gallery (foto cucina)
};

const Services = () => {
  const { t, lang } = useLang();
  const { content } = useSiteContent();
  const [notifyOpen, setNotifyOpen] = useState(null);
  const [infoOpen, setInfoOpen] = useState(null);

  // Use CMS list if available; fall back to i18n list
  const cmsList = content?.services;
  let list = cmsList?.length
    ? cmsList.map((s) => ({
        icon: s.icon,
        t: s.t?.[lang] || s.t?.it || "",
        d: s.d?.[lang] || s.d?.it || "",
        info: s.info?.[lang] || s.info?.it || "",
        url: s.url || DEFAULT_LINKS[s.icon] || "",
        whatsapp: s.whatsapp || "",
        notify_host: !!s.notify_host,
      }))
    : (t.services.list || []).map((s) => ({ ...s, url: DEFAULT_LINKS[s.icon] || "" }));

  // iter207 — Assicura che la card "IVA/tasse/supplementi" sia sempre presente
  // (anche se l'admin ha personalizzato la lista dal CMS senza aggiungerla).
  // La card mostra le regole di IVA e tassa di soggiorno nella lingua del
  // visitatore, riprendendole da `t.services.list` (definite in i18n.js).
  if (!list.some((s) => s.icon === "tax")) {
    const taxFromI18n = (t.services.list || []).find((s) => s.icon === "tax");
    if (taxFromI18n) list = [...list, { ...taxFromI18n, url: "" }];
  }

  return (
    <section id="services" className="py-20 md:py-28 bg-[#FDFBF7]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12">
        <div className="max-w-2xl mb-14">
          <p className="text-overline mb-3">{t.services.overline}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#1E3A5F] leading-tight">{t.services.title}</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {list.map((s, i) => {
            const Icon = ICONS[s.icon] || Sparkles;
            const waLink = s.whatsapp
              ? (s.whatsapp.startsWith("http") ? s.whatsapp : `https://wa.me/${s.whatsapp.replace(/[^\d+]/g, "").replace(/^\+/, "")}`)
              : null;
            const isExternal = s.url && /^https?:\/\//.test(s.url);
            const hasAnyAction = !!(s.url || s.notify_host || waLink || s.info);
            return (
              <button
                type="button"
                key={`${s.icon}-${i}`}
                data-testid={`service-${s.icon}`}
                onClick={() => setInfoOpen({ ...s, _idx: i, waLink, isExternal })}
                className="group relative bg-white border border-[#E8E2D9] rounded-xl p-6 hover:border-[#AC5D45] hover:-translate-y-1 transition-all duration-300 text-left cursor-pointer w-full focus:outline-none focus:ring-2 focus:ring-[#AC5D45]/40"
              >
                <div className="w-12 h-12 rounded-full bg-[#AC5D45]/10 flex items-center justify-center mb-4 group-hover:bg-[#AC5D45]/20 transition">
                  <Icon className="w-5 h-5 text-[#AC5D45]" />
                </div>
                {hasAnyAction && (
                  <ExternalLink className="absolute top-4 right-4 w-3.5 h-3.5 text-[#AC5D45] opacity-50 group-hover:opacity-100 transition" />
                )}
                <h3 className="font-display text-xl text-[#1E3A5F] mb-2 group-hover:text-[#AC5D45] transition">{s.t}</h3>
                <p className="text-sm text-[#635F59] leading-relaxed">{s.d}</p>
              </button>
            );
          })}
        </div>
      </div>
      {notifyOpen && <ServiceRequestModal service={notifyOpen} onClose={() => setNotifyOpen(null)} />}
      {infoOpen && (
        <ServiceInfoModal
          service={infoOpen}
          onClose={() => setInfoOpen(null)}
          onAvvisaHost={() => { const s = infoOpen; setInfoOpen(null); setNotifyOpen({ ...s }); }}
        />
      )}
    </section>
  );
};

export default Services;


/* Modal that lets the guest leave name/room/date/time/notes for a service
   that needs advance notice (hairdresser, delivery, etc.). Submits to the
   backend which fires Telegram + email to the host. */
function ServiceRequestModal({ service, onClose }) {
  const [form, setForm] = useState({
    name: "", room: "", phone: "",
    preferred_date: new Date().toISOString().slice(0, 10),
    preferred_time: "09:00",
    notes: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) { alert("Inserisci il nome"); return; }
    setSending(true);
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
      const fetchRes = await fetch(`${API}/api/services/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service_icon: service.icon,
          service_label: service.t,
          ...form,
        }),
      });
      if (!fetchRes.ok) throw new Error(`HTTP ${fetchRes.status}`);
      setDone(true);
    } catch (e) {
      alert("Errore nell'invio. Riprova o contatta l'host via WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="service-request-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {done ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <span className="text-3xl">✓</span>
            </div>
            <h3 className="font-display text-xl text-[#1E3A5F] mb-2">Richiesta inviata!</h3>
            <p className="text-sm text-[#635F59] mb-4">
              L&apos;host è stato notificato e si organizzerà per garantirti il servizio. Ti ricontatterà a breve.
            </p>
            <button onClick={onClose} className="px-5 py-2 bg-[#AC5D45] text-white rounded-full text-sm">Chiudi</button>
          </div>
        ) : (
          <>
            <h3 className="font-display text-xl text-[#1E3A5F] mb-1">Richiedi: {service.t}</h3>
            <p className="text-xs text-[#635F59] mb-4">L&apos;host sarà notificato (email + Telegram) per organizzarsi.</p>
            <div className="space-y-2">
              <input
                placeholder="Il tuo nome *"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-[#E8E2D9] text-sm"
                data-testid="srvc-req-name"
              />
              <input
                placeholder="Numero camera (es. Matrimoniale)"
                value={form.room}
                onChange={(e) => setForm({ ...form, room: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-[#E8E2D9] text-sm"
                data-testid="srvc-req-room"
              />
              <input
                placeholder="Telefono (opzionale)"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-[#E8E2D9] text-sm"
                data-testid="srvc-req-phone"
              />
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="px-3 py-2 rounded-lg border border-[#E8E2D9] text-sm" data-testid="srvc-req-date" />
                <input type="time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className="px-3 py-2 rounded-lg border border-[#E8E2D9] text-sm" data-testid="srvc-req-time" />
              </div>
              <textarea
                rows={3}
                placeholder="Note (es. allergie, preferenze)"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-[#E8E2D9] text-sm"
                data-testid="srvc-req-notes"
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="px-4 py-2 text-sm rounded-full border border-[#E8E2D9]">Annulla</button>
              <button onClick={submit} disabled={sending} className="px-5 py-2 text-sm bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full disabled:opacity-50" data-testid="srvc-req-submit">
                {sending ? "Invio…" : "Invia richiesta"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}



/* Service detail popup — opens when the guest clicks anywhere on a
   service card. Shows the icon, title, short description, optional
   long info text (whitespace preserved), and the action buttons
   (Prenota il servizio + Avvisa l'Host) when configured by the host.
   Single unified panel — keeps the cards visually clean. */
function ServiceInfoModal({ service, onClose, onAvvisaHost }) {
  const s = service || {};
  const Icon = ICONS[s.icon] || Sparkles;
  const waLink = s.waLink;
  // Stessi pulsanti delle card originali: appaiono SOLO con WhatsApp o
  // URL esterno (link interni come #gallery non generano alcun pulsante
  // — manteniamo il comportamento di prima, solo spostato nel popup).
  const hasBookAction = !!(waLink || (s.url && s.isExternal));
  const bookHref = waLink || s.url;
  const bookGreen = !!waLink;
  const hasInfo = !!(s.info && String(s.info).trim());
  const hasShortDesc = !!(s.d && String(s.d).trim());

  return (
    <div
      className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="service-info-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2A4F7C] text-white px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <h3 className="font-display text-xl flex-1 truncate" data-testid="service-info-title">{s.t}</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
            aria-label="Chiudi"
            data-testid="service-info-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto space-y-3">
          {hasShortDesc && (
            <p className="text-sm text-[#1E3A5F] font-medium leading-relaxed" data-testid="service-info-tagline">
              {s.d}
            </p>
          )}
          {hasInfo && (
            <p className="text-sm text-[#2D2926] leading-relaxed whitespace-pre-line" data-testid="service-info-body">
              {s.info}
            </p>
          )}
          {!hasShortDesc && !hasInfo && (
            <p className="text-sm text-[#8A8680] italic">Nessun dettaglio aggiuntivo disponibile.</p>
          )}
        </div>

        <div className="px-6 py-4 bg-[#FDFBF7] border-t border-[#E8E2D9] flex flex-wrap gap-2 justify-end">
          {hasBookAction && (
            <a
              href={bookHref}
              target="_blank"
              rel="noopener noreferrer"
              className={`text-xs uppercase tracking-wider font-bold px-4 py-2 rounded-full text-white inline-flex items-center gap-1.5 ${bookGreen ? "bg-[#25D366] hover:bg-[#1ea354]" : "bg-[#AC5D45] hover:bg-[#8A4936]"}`}
              data-testid={`service-modal-book-${s.icon}`}
            >
              {bookGreen ? "💬 Prenota il servizio" : "🔗 Prenota il servizio"}
            </a>
          )}
          {s.notify_host && (
            <button
              type="button"
              onClick={onAvvisaHost}
              className="text-xs uppercase tracking-wider font-bold px-4 py-2 rounded-full bg-white border border-[#AC5D45] text-[#AC5D45] hover:bg-[#AC5D45]/10 inline-flex items-center gap-1.5"
              data-testid={`service-modal-request-${s.icon}`}
            >
              🔔 Avvisa l&apos;Host
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase tracking-wider font-bold px-4 py-2 rounded-full bg-[#1E3A5F] hover:bg-[#162A45] text-white"
            data-testid="service-info-ok"
          >
            Ho capito
          </button>
        </div>
      </div>
    </div>
  );
}
